<script setup>
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { onClickOutside } from '@vueuse/core'; // Se disponível, ou implemente manualmente

import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  total: {
    type: Number,
    required: true,
  },
  table: {
    type: Object,
    required: true,
  },
  defaultPageSize: {
    type: Number,
    default: 10,
  },
  showPageSizeSelector: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['pageSizeChange']);
const { t } = useI18n();

// Controle do dropdown personalizado
const isDropdownOpen = ref(false);
const dropdownRef = ref(null);

// Fechar dropdown quando clicar fora
if (typeof onClickOutside === 'function') {
  onClickOutside(dropdownRef, () => {
    isDropdownOpen.value = false;
  });
}

const pageSizeOptions = [
  {
    label: `10 por página`,
    value: 10,
  },
  {
    label: `20 por página`,
    value: 20,
  },
  {
    label: `50 por página`,
    value: 50,
  },
];

const toggleDropdown = () => {
  isDropdownOpen.value = !isDropdownOpen.value;
};

const selectPageSize = (size) => {
  props.table.setPageSize(Number(size));
  emit('pageSizeChange', Number(size));
  isDropdownOpen.value = false;
};

const getFormattedPages = (start, end) => {
  const formatter = new Intl.NumberFormat(navigator.language);
  return Array.from({ length: end - start + 1 }, (_, i) =>
    formatter.format(start + i)
  );
};

const currentPage = computed(() => {
  return props.table.getState().pagination.pageIndex + 1;
});

const totalPages = computed(() => {
  return props.table.getPageCount();
});

const visiblePages = computed(() => {
  if (totalPages.value <= 3) return getFormattedPages(1, totalPages.value);
  if (currentPage.value === 1) return getFormattedPages(1, 3);
  if (currentPage.value === totalPages.value) {
    return getFormattedPages(totalPages.value - 2, totalPages.value);
  }

  return getFormattedPages(currentPage.value - 1, currentPage.value + 1);
});

const total = computed(() => {
  return props.total;
});

const start = computed(() => {
  const { pagination } = props.table.getState();
  return pagination.pageIndex * pagination.pageSize + 1;
});

const end = computed(() => {
  const { pagination } = props.table.getState();
  return Math.min(
    (pagination.pageIndex + 1) * pagination.pageSize,
    total.value
  );
});

const currentPageSize = computed(() => {
  return props.table.getState().pagination.pageSize;
});

onMounted(() => {
  if (
    props.showPageSizeSelector &&
    props.defaultPageSize &&
    props.defaultPageSize !== 10
  ) {
    props.table.setPageSize(props.defaultPageSize);
  }
  
  // Implementação manual de click outside se @vueuse/core não estiver disponível
  if (typeof onClickOutside !== 'function') {
    const handleClickOutside = (event) => {
      if (dropdownRef.value && !dropdownRef.value.contains(event.target)) {
        isDropdownOpen.value = false;
      }
    };
    
    document.addEventListener('click', handleClickOutside);
    
    // Limpar o event listener quando o componente for desmontado
    onUnmounted(() => {
      document.removeEventListener('click', handleClickOutside);
    });
  }
});
</script>

<template>
  <div class="flex items-center justify-between">
    <div class="flex flex-1 items-center gap-2 justify-between">
      <p class="text-sm truncate text-n-slate-11 mb-0">
        Mostrando {{ start }} a {{ end }} de {{ total }} resultados
      </p>
      <div class="flex items-center gap-2">
        <!-- Implementação personalizada do seletor de tamanho de página -->
        <div v-if="showPageSizeSelector" class="relative" ref="dropdownRef">
          <button
            type="button"
            class="inline-flex items-center gap-1 px-2 py-1 h-6 text-sm font-medium text-n-slate-11 hover:text-n-slate-12 bg-white dark:bg-slate-800 border border-n-weak rounded-md hover:bg-n-weak"
            @click="toggleDropdown"
          >
            {{ currentPageSize }}
            <span class="i-lucide-chevron-down text-xs"></span>
          </button>
          
          <!-- Dropdown que abre para cima -->
          <div
            v-if="isDropdownOpen"
            class="absolute z-50 bottom-full left-0 mb-1 w-auto min-w-32 rounded-md bg-white dark:bg-slate-800 shadow-lg border border-n-weak"
          >
            <div class="p-1">
              <button
                v-for="option in pageSizeOptions"
                :key="option.value"
                type="button"
                class="w-full px-4 py-2 text-left text-sm hover:bg-n-weak rounded-sm"
                :class="{ 'font-medium': currentPageSize === option.value }"
                @click="selectPageSize(option.value)"
              >
                {{ option.label }}
              </button>
            </div>
          </div>
        </div>
        
        <nav class="isolate inline-flex items-center gap-1.5">
          <Button
            icon="i-lucide-chevrons-left"
            ghost
            slate
            sm
            class="!size-6"
            :disabled="!table.getCanPreviousPage()"
            @click="table.setPageIndex(0)"
          />
          <Button
            icon="i-lucide-chevron-left"
            ghost
            slate
            sm
            class="!size-6"
            :disabled="!table.getCanPreviousPage()"
            @click="table.previousPage()"
          />
          <Button
            v-for="page in visiblePages"
            :key="page"
            xs
            outline
            :color="page == currentPage ? 'blue' : 'slate'"
            class="!h-6 min-w-6"
            @click="table.setPageIndex(page - 1)"
          >
            <span
              class="text-center"
              :class="{ 'text-n-brand': page == currentPage }"
            >
              {{ page }}
            </span>
          </Button>
          <Button
            icon="i-lucide-chevron-right"
            ghost
            slate
            sm
            class="!size-6"
            :disabled="!table.getCanNextPage()"
            @click="table.nextPage()"
          />
          <Button
            icon="i-lucide-chevrons-right"
            ghost
            slate
            sm
            class="!size-6"
            :disabled="!table.getCanNextPage()"
            @click="table.setPageIndex(table.getPageCount() - 1)"
          />
        </nav>
      </div>
    </div>
  </div>
</template>